import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useCallback, useEffect, useState} from 'react';
import {Connection, ConnectionLineComponentProps} from 'reactflow';

import {assert} from 'Asserts';
import {makeCompatibleMaterialInputSetting} from 'procedures/design/materialEdgeUtils';
import {
  MethodNode,
  ProcedureEdge,
  ProcedureEdgeType,
  ProcedureFlowchart,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
} from 'procedures/ProcedureDomainTypes';
import {
  ProcedurePageContext,
  BlockingSaveObjectSource,
  BlockingSaveOperation,
} from 'procedures/ProcedurePageContext';

import {extractMethodEdges, isMaterialEdgeFlowingDownstream} from './flowchartManagementUtils';
import {createEdgeId, getEdgeTypeFromHandleId, getMaterialCidFromHandle} from './handleIdentifierUtils';
import ProcedureMaterialEdgeConnectionLine from './ProcedureMaterialEdgeConnectionLine';
import {nodeZIndex} from './procedureMethodNodeCommonConsts';
import {ModifyFlowchartCallback} from './useProcedureActions';

export interface UseProcedureProcedureMaterialEdgeConnectionOutput {
  handleConnectStart: () => void;
  handleConnectEnd: () => void;
  handleConnect: (connection: Connection) => void;
  materialEdgeConnectionLine: (connectionProps: ConnectionLineComponentProps) => JSX.Element;
}

function findMaterialInputSetting(
  flowchart: ProcedureFlowchart,
  methodCid: string,
  materialCid: string
): ProcedureMaterialInputSetting {
  const methodNode = flowchart.nodes.find((node) => node.cid === methodCid);
  const materialSetting = methodNode
    ? methodNode.materialInputSettings.find((setting) => setting.materialInputVersion.cid === materialCid)
    : null;
  assert(methodNode != null && materialSetting != null, 'Unable to find material', {
    methodCid,
    materialCid,
    flowchart,
  });
  return materialSetting;
}

/**
 * Modifies a flowchart to ensure that the given material input setting is
 * compatible with the source material output setting.
 *
 * We also remove any planned values associated with the target material input,
 * as the values are no longer meaningful for a process intermediate.
 *
 * @package exported for testing
 */
export function forceSetMaterialInputSettingToBeCompatible(
  sourceMaterialOutpuSetting: ProcedureMaterialOutputSetting,
  originalMaterialInputSetting: ProcedureMaterialInputSetting,
  flowchartNodes: Array<MethodNode>,
  targetMethodCid: string
): Array<MethodNode> {
  const targetMaterialCid = originalMaterialInputSetting.materialInputVersion.cid;

  // We adjust the material input setting to match the material output setting. This also forces
  // any default material (and amount) to be removed
  const compatibleInputSetting = makeCompatibleMaterialInputSetting(
    sourceMaterialOutpuSetting,
    originalMaterialInputSetting
  );
  return produce(flowchartNodes, (draftNodes) => {
    draftNodes.map((node) => {
      if (node.cid === targetMethodCid) {
        node.materialInputSettings = node.materialInputSettings.map((inputSetting) =>
          inputSetting.materialInputVersion.cid === targetMaterialCid ? compatibleInputSetting : inputSetting
        );
      }
      return node;
    });
  });
}

/**
 * Encapsulates all state and callbacks needed to support creating a material edge via a connection line
 */
export default function useLegacyProcedureMaterialEdgeConnection(
  flowchart: ProcedureFlowchart,
  modifyFlowchart: (cb: ModifyFlowchartCallback) => void,
  materialOutputSettingBeingConnected: ProcedureMaterialOutputSetting | null,
  reactFlowWrapper: React.RefObject<HTMLDivElement>
): UseProcedureProcedureMaterialEdgeConnectionOutput {
  const [isActivelyConnecting, setIsActivelyConnecting] = useState<boolean>(false);
  const {legacySaveImmediately} = ProcedurePageContext.useContext();

  useEffect(() => {
    // ReactFlow places custom nodes and edges into SVG elements
    // Which restricts our ability to change the zIndex in our custom components
    // Here we are reaching into the DOM to modify the ReactFlow component directly
    if (reactFlowWrapper.current != null && isActivelyConnecting) {
      const connectionLines = reactFlowWrapper.current.getElementsByClassName('react-flow__connectionline');
      if (connectionLines.length > 0) {
        const connectionLine = connectionLines[0];
        connectionLine.classList.add(css(styles.highZIndex));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivelyConnecting]);

  const handleConnectStart = useCallback(() => {
    setIsActivelyConnecting(true);
  }, []);

  const handleConnectEnd = useCallback(() => {
    // OnConnect triggers one more time after onConnectend, so must track active connection status manually
    setIsActivelyConnecting(false);
  }, []);

  const handleConnect = useCallback(
    async ({
      source: sourceMethodCid,
      target: targetMethodCid,
      sourceHandle,
      targetHandle,
    }: Connection): Promise<void> => {
      assert(
        sourceMethodCid != null && targetMethodCid != null && sourceHandle != null && targetHandle != null,
        'Invalid connection'
      );

      if (
        !isMaterialEdgeFlowingDownstream(
          sourceMethodCid,
          targetMethodCid,
          extractMethodEdges(flowchart.edges)
        )
      ) {
        // We do not currently allow new material connections to be made going upstream
        return;
      }

      const newEdgeId = createEdgeId(sourceHandle, targetHandle);
      if (flowchart.edges.find((edge) => edge.id === newEdgeId)) {
        // Prevent duplicate edges
        return;
      }

      const edgeType = getEdgeTypeFromHandleId(targetHandle);
      assert(edgeType === ProcedureEdgeType.MATERIAL_EDGE, 'Expected a material edge connection', {
        source: sourceMethodCid,
        target: targetMethodCid,
      });
      const targetMaterialCid = getMaterialCidFromHandle(targetHandle);
      const targetMaterialInputSetting = findMaterialInputSetting(
        flowchart,
        targetMethodCid,
        targetMaterialCid
      );
      assert(materialOutputSettingBeingConnected != null, 'Expected a valid output setting');

      const newEdge: ProcedureEdge = {
        id: newEdgeId,
        fromMethodCid: sourceMethodCid,
        toMethodCid: targetMethodCid,
        type: edgeType,
        sourceHandle,
        targetHandle,
      };

      await legacySaveImmediately((latestFlowchart) => {
        modifyFlowchart((fc) => {
          const flowchartNodes = forceSetMaterialInputSettingToBeCompatible(
            materialOutputSettingBeingConnected,
            targetMaterialInputSetting,
            latestFlowchart.nodes,
            targetMethodCid
          );
          return {nodes: flowchartNodes, edges: [...flowchart.edges, newEdge]};
        });
        return {
          blockingSaveObjectSource: BlockingSaveObjectSource.MaterialEdge,
          blockingSaveOperation: BlockingSaveOperation.Create,
        };
      });
    },
    [flowchart, materialOutputSettingBeingConnected, legacySaveImmediately, modifyFlowchart]
  );

  const materialEdgeConnectionLine = useCallback(
    (connectionProps: ConnectionLineComponentProps) => (
      <ProcedureMaterialEdgeConnectionLine {...connectionProps} isActivelyConnecting={isActivelyConnecting} />
    ),
    [isActivelyConnecting]
  );

  return {
    handleConnectStart,
    handleConnectEnd,
    handleConnect,
    materialEdgeConnectionLine,
  };
}

const styles = StyleSheet.create({
  highZIndex: {
    zIndex: nodeZIndex + 1,
  },
});
